const COOKIEBOT_META_NAME = 'cookiebot-cbid';
const COOKIE_MESSAGE_SELECTOR = '.cookie-message';
const VIDEO_BLOCK_CONTAINER_SELECTOR = '.video-block__container';
const VIDEO_IFRAME_SELECTOR = 'iframe';
const DATA_SRC_ATTRIBUTE = 'data-src';

declare global {
  interface Window {
    Cookiebot: {
      consent?: {
        preferences: boolean;
        statistics: boolean;
        marketing: boolean;
      };
      hasResponse?: boolean;
      renew?: () => void;
    };
    dataLayer: Array<Record<string, string | boolean>>;

    __nativeFetch?: typeof window.fetch;
  }
}

const enableVideoFunctionality = (): void => {
  const cookieMessages = document.querySelectorAll(COOKIE_MESSAGE_SELECTOR);
  cookieMessages.forEach((message) => {
    message.remove();
  });

  const videoContainers = document.querySelectorAll(VIDEO_BLOCK_CONTAINER_SELECTOR);
  if (videoContainers.length > 0) {
    const videoIframes = document.querySelectorAll<HTMLIFrameElement>(`${VIDEO_BLOCK_CONTAINER_SELECTOR} ${VIDEO_IFRAME_SELECTOR}`);

    videoIframes.forEach((iframe) => {
      const dataSrc = iframe.getAttribute(DATA_SRC_ATTRIBUTE);
      if (dataSrc) {
        iframe.src = dataSrc;
      }
    });
  }
};

const handleCookieAccept = (): void => {
  if (!window.Cookiebot?.consent) {
    return;
  }
  if (window.Cookiebot.consent.preferences) {
    window.dataLayer.push({
      event: 'cookieconsent_preferences',
      preferencesStatus: 'true'
    });
  }

  if (window.Cookiebot.consent.statistics) {
    window.dataLayer.push({
      event: 'cookieconsent_statistics',
      statisticsStatus: 'true'
    });
  }

  if (window.Cookiebot.consent.marketing) {
    window.dataLayer.push({
      event: 'cookieconsent_marketing',
      marketingStatus: 'true'
    });
    enableVideoFunctionality();
  }
};

const handleCookieDecline = (): void => {
  window.dataLayer.push({
    event: 'cookieconsent_declineAll',
    preferencesStatus: 'false',
    statisticsStatus: 'false',
    marketingStatus: 'false'
  });
};

const getCookiebotIdFromMeta = (): string | null => {
  const meta = document.querySelector(`meta[name="${COOKIEBOT_META_NAME}"]`);
  const id = meta?.getAttribute('content')?.trim();
  return id || null;
};

const injectCookiebotScript = (cbid: string): HTMLScriptElement | null => {
  const existing = document.getElementById('Cookiebot') as HTMLScriptElement | null;
  if (existing) {
    return existing;
  }
  const script = document.createElement('script');
  script.id = 'Cookiebot';
  script.src = 'https://consent.cookiebot.com/uc.js';
  script.type = 'text/javascript';
  script.setAttribute('data-cbid', cbid);
  script.async = true;
  document.head.appendChild(script);
  return script;
};

const scheduleCookiebotAfterPageLoad = (cbid: string): void => {
  const run = () => injectCookiebotScript(cbid);
  if (document.readyState === 'complete') {
    run();
  } else {
    window.addEventListener('load', run, { once: true });
  }
};

const installCookiebotStub = (cbid: string): void => {
  const stub: Window['Cookiebot'] = {
    renew: (): void => {
      const real = window.Cookiebot;
      if (real !== stub && typeof real?.renew === 'function') {
        real.renew();
        return;
      }

      const scriptEl = injectCookiebotScript(cbid);
      if (!scriptEl) {
        return;
      }

      let settled = false;
      let pollId: number | null = null;

      const callRealRenew = (): void => {
        if (settled) {
          return;
        }
        const cb = window.Cookiebot;
        if (cb !== stub && typeof cb?.renew === 'function') {
          settled = true;
          if (pollId !== null) {
            window.clearInterval(pollId);
            pollId = null;
          }
          cb.renew();
          return;
        }
        if (pollId !== null) {
          return;
        }
        let attempts = 0;
        pollId = window.setInterval(() => {
          attempts++;
          const next = window.Cookiebot;
          if (next !== stub && typeof next?.renew === 'function') {
            settled = true;
            if (pollId !== null) {
              window.clearInterval(pollId);
              pollId = null;
            }
            next.renew();
          } else if (attempts >= 200) {
            if (pollId !== null) {
              window.clearInterval(pollId);
              pollId = null;
            }
          }
        }, 50);
      };

      scriptEl.addEventListener('load', callRealRenew, { once: true });
      scriptEl.addEventListener('error', callRealRenew, { once: true });

      requestAnimationFrame(() => callRealRenew());
    }
  };

  window.Cookiebot = stub;
};

/** dataLayer consent listeners — Cookiebot script is expected in Head.cshtml. */
export function prepareCookiebotListeners(): void {
  window.addEventListener('CookiebotOnAccept', handleCookieAccept, false);
  window.addEventListener('CookiebotOnDecline', handleCookieDecline, false);
}

/**
 * Stub + listeners when Cookiebot is not already in the document (fallback path only).
 * Prefer Head.cshtml early install; do not overwrite a live Cookiebot object.
 */
export function prepareCookiebotStubAndListeners(cbid: string): void {
  if (!document.getElementById('Cookiebot') && typeof window.Cookiebot?.renew !== 'function') {
    installCookiebotStub(cbid);
  }
  prepareCookiebotListeners();
}

export { injectCookiebotScript };

const init = (): void => {
  if ((window as Window & { __deferredThirdPartyInit?: boolean }).__deferredThirdPartyInit) {
    return;
  }

  const cbid = getCookiebotIdFromMeta();
  if (!cbid) {
    enableVideoFunctionality();
    return;
  }

  // Head already has Cookiebot — only wire listeners. Otherwise fall back to late inject.
  if (document.getElementById('Cookiebot')) {
    prepareCookiebotListeners();
    return;
  }

  prepareCookiebotStubAndListeners(cbid);
  scheduleCookiebotAfterPageLoad(cbid);
};

export { init };
